import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Avatar, Badge, Button, Card, EmptyState, Icon, Input, Sheet, Skeleton } from '@astral/ui';
import type { AdminPaymentRequestDTO, PaymentStatus } from '@astral/shared';
import { api, ApiClientError } from '../lib/api.js';
import { useAuth } from '../providers/AuthProvider.js';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';
import { useToast } from '../providers/ToastProvider.js';
import { ConfirmSheet } from '../components/ConfirmSheet.js';
import { formatRelative } from '../lib/format.js';

const TABS: Array<{ key: string; status?: PaymentStatus }> = [
  { key: 'stRECEIPT_SUBMITTED', status: 'RECEIPT_SUBMITTED' },
  { key: 'stCOMPLETED', status: 'COMPLETED' },
  { key: 'stAPPROVED', status: 'APPROVED' },
  { key: 'stREJECTED', status: 'REJECTED' },
  { key: 'filterAll', status: undefined },
];

const LOCALE: Record<string, string> = { en: 'en-US', fa: 'fa-IR', ru: 'ru-RU' };

/** Admin-only Premium payment review queue: approve/reject + receipt viewer. */
export function AdminPaymentsScreen(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const allowed = hasPermission('admin:manage_payments');

  const [tab, setTab] = useState(0);
  const [items, setItems] = useState<AdminPaymentRequestDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState<AdminPaymentRequestDTO | null>(null);
  const [rejectTarget, setRejectTarget] = useState<AdminPaymentRequestDTO | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [receiptUrl, setReceiptUrl] = useState<string | null>(null);
  const [receiptLoading, setReceiptLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    api
      .adminListPayments(TABS[tab].status)
      .then((r) => setItems(r.items))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [tab]);

  useEffect(() => {
    if (!allowed) {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [allowed, load, navigate]);

  const approve = async (): Promise<void> => {
    if (!confirmApprove) return;
    setBusy(true);
    try {
      await api.adminApprovePayment(confirmApprove.id);
      toast.show(t('admin.approved'), 'success');
      setConfirmApprove(null);
      load();
    } catch (err) {
      toast.show(err instanceof ApiClientError ? err.message : t('errors.generic'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (): Promise<void> => {
    if (!rejectTarget) return;
    setBusy(true);
    try {
      await api.adminRejectPayment(rejectTarget.id, rejectReason.trim() || undefined);
      toast.show(t('admin.rejected'), 'default');
      setRejectTarget(null);
      setRejectReason('');
      load();
    } catch (err) {
      toast.show(err instanceof ApiClientError ? err.message : t('errors.generic'), 'error');
    } finally {
      setBusy(false);
    }
  };

  const openReceipt = async (item: AdminPaymentRequestDTO): Promise<void> => {
    setReceiptLoading(true);
    setReceiptUrl('');
    try {
      const url = await api.adminReceiptObjectUrl(item.id);
      setReceiptUrl(url);
    } catch {
      toast.show(t('errors.loadFailed'), 'error');
      setReceiptUrl(null);
    } finally {
      setReceiptLoading(false);
    }
  };
  const closeReceipt = (): void => {
    if (receiptUrl) URL.revokeObjectURL(receiptUrl);
    setReceiptUrl(null);
  };

  const statusTone = (s: PaymentStatus): 'success' | 'warning' | 'danger' | 'neutral' =>
    s === 'APPROVED' || s === 'COMPLETED'
      ? 'success'
      : s === 'RECEIPT_SUBMITTED' || s === 'AWAITING_PAYMENT'
        ? 'warning'
        : s === 'REJECTED' || s === 'EXPIRED' || s === 'FAILED'
          ? 'danger'
          : 'neutral';
  const amountLabel = (item: AdminPaymentRequestDTO): string =>
    item.currency === 'XTR'
      ? `⭐ ${item.price}`
      : `${new Intl.NumberFormat(LOCALE[language] ?? 'en-US').format(item.price)} ${t('premium.toman')}`;

  return (
    <div className="app-root">
      <AppBar title={t('admin.reviewTitle')} onBack={() => navigate(-1)} />
      <div className="screen">
        <div className="admin-filters">
          {TABS.map((tb, i) => (
            <button key={tb.key} className={`chip${tab === i ? ' chip--active' : ''}`} onClick={() => setTab(i)}>
              {t(`admin.${tb.key}` as TranslationKey)}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="stack">
            <Skeleton height={120} radius={16} />
            <Skeleton height={120} radius={16} />
          </div>
        ) : error ? (
          <EmptyState icon={<Icon name="shield" size={28} />} title={t('errors.loadFailed')} action={<Button onClick={load}>{t('common.retry')}</Button>} />
        ) : items.length === 0 ? (
          <EmptyState icon={<Icon name="bell" size={28} />} title={t('admin.queueEmpty')} />
        ) : (
          <div className="stack">
            {items.map((item) => (
              <Card key={item.id} padding="md" className="stack" style={{ gap: 10 }}>
                <div className="row-between">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <Avatar name={item.user.firstName} src={item.user.photoUrl} size={40} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.user.firstName}
                      </div>
                      <div className="muted" style={{ fontSize: 12 }}>
                        {item.user.username ? `@${item.user.username}` : item.user.telegramId}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <Badge tone={item.method === 'TELEGRAM_STARS' ? 'accent' : 'neutral'}>
                      {item.method === 'TELEGRAM_STARS' ? t('admin.methodStars') : t('admin.methodToman')}
                    </Badge>
                    <Badge tone={statusTone(item.status)}>{t(`admin.st${item.status}` as TranslationKey)}</Badge>
                  </div>
                </div>

                <div className="payment-meta">
                  <span>{t('admin.plan')}: <b>{t(`premium.month${item.months}` as TranslationKey)}</b></span>
                  <span>{t('admin.price')}: <b>{amountLabel(item)}</b></span>
                  <span className="muted">{t('admin.requested')}: {formatRelative(item.createdAt, language)}</span>
                  {item.telegramChargeId && (
                    <span className="muted" style={{ fontSize: 11 }}>{t('admin.chargeId')}: {item.telegramChargeId.slice(0, 18)}…</span>
                  )}
                </div>

                <div className="stack" style={{ gap: 8 }}>
                  {/* Receipt + review only for MANUAL Toman; Stars is auto-completed. */}
                  {item.method === 'TOMAN_MANUAL' && (
                    <Button
                      variant="secondary"
                      fullWidth
                      disabled={!item.hasReceipt}
                      leftIcon={<Icon name="image" size={16} />}
                      onClick={() => void openReceipt(item)}
                    >
                      {item.hasReceipt ? t('admin.viewReceipt') : t('admin.noReceipt')}
                    </Button>
                  )}
                  {item.status === 'RECEIPT_SUBMITTED' && (
                    <div className="row-between" style={{ gap: 8 }}>
                      <Button fullWidth leftIcon={<Icon name="check" size={16} />} onClick={() => setConfirmApprove(item)}>
                        {t('admin.approve')}
                      </Button>
                      <Button fullWidth variant="ghost" onClick={() => setRejectTarget(item)}>
                        {t('admin.reject')}
                      </Button>
                    </div>
                  )}
                  {item.status === 'REJECTED' && item.rejectionReason && (
                    <p className="muted" style={{ fontSize: 12 }}>{item.rejectionReason}</p>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmSheet
        open={Boolean(confirmApprove)}
        title={t('admin.approve')}
        message={t('admin.approveConfirm')}
        confirmLabel={t('admin.approve')}
        loading={busy}
        onConfirm={() => void approve()}
        onCancel={() => setConfirmApprove(null)}
      />

      <Sheet open={Boolean(rejectTarget)} onClose={() => setRejectTarget(null)} title={t('admin.reject')}>
        <div className="stack" style={{ gap: 12 }}>
          <p className="muted">{t('admin.rejectConfirm')}</p>
          <Input
            label={t('admin.rejectReason')}
            value={rejectReason}
            maxLength={200}
            onChange={(e) => setRejectReason(e.target.value)}
          />
          <Button fullWidth size="lg" variant="danger" loading={busy} onClick={() => void reject()}>
            {t('admin.reject')}
          </Button>
        </div>
      </Sheet>

      <Sheet open={receiptUrl !== null} onClose={closeReceipt} title={t('admin.receipt')}>
        {receiptLoading || receiptUrl === '' ? (
          <Skeleton height={280} radius={12} />
        ) : receiptUrl ? (
          <img className="payment-receipt" src={receiptUrl} alt={t('admin.receipt')} />
        ) : null}
      </Sheet>
    </div>
  );
}
